import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { isAxiosError } from 'axios';
import { useCustomToast, isNetworkError } from '@/shared/lib';
import { documentApi } from '@/entities/Document';
import { MIN_DOCUMENT_TITLE_LENGTH } from '@/shared/config';
import useFileValidation from '../useFileValidation';
import { usePrefixConflictCheck } from '../usePrefixConflictCheck';
import { buildUniquePrefixList } from '../buildUniquePrefixList';
import type { FileUploadCallbacks, DeleteResult } from './types';

const NETWORK_ERROR_MESSAGE_SINGULAR = 'Erro ao enviar documento, tente novamente.';
const NETWORK_ERROR_MESSAGE_PLURAL = 'Erro ao enviar documentos, tente novamente.';
const SEND_ERROR_TITLE_SINGULAR = 'Erro ao enviar documento';
const SEND_ERROR_TITLE_PLURAL = 'Erro ao enviar documentos';
const EXTERNAL_DOCUMENT_TITLE_ALREADY_EXISTS_MESSAGE =
  'Já existe um documento com o mesmo título no sistema.';

function isNetworkErrorLike(error: unknown): boolean {
  if (isNetworkError(error)) return true;
  return (
    error instanceof Error &&
    (error.message?.includes('Network Error') ?? false)
  );
}

function getNetworkErrorMessage(count: number): string {
  return count === 1 ? NETWORK_ERROR_MESSAGE_SINGULAR : NETWORK_ERROR_MESSAGE_PLURAL;
}

function getSendErrorTitle(count: number): string {
  return count === 1 ? SEND_ERROR_TITLE_SINGULAR : SEND_ERROR_TITLE_PLURAL;
}

export default defineStore('fileUpload', () => {
  // ============================================
  // STATE
  // ============================================

  // File ID counter to prevent collisions
  let fileIdCounter = 0;

  // File Management
  const files = ref<File[]>([]);
  const uploadedFiles = ref<File[]>([]);
  const filesIds = ref<Map<File, string>>(new Map());

  // Upload Status
  const hasFiles = ref(false);
  const isUrlUploadValid = ref(false);
  const filesLoaded = ref(false);
  const isUploading = ref(false);

  // Progress Tracking
  const fileProgress = ref<Map<string, number>>(new Map());
  const uploadProgress = ref<Map<string, number>>(new Map());
  const uploadStatus = ref<Map<string, 'pending' | 'uploading' | 'completed' | 'error'>>(new Map());

  // Document Tracking
  const uploadedDocumentIds = ref<number[]>([]);
  const fileToDocumentIdMap = ref<Map<string, number>>(new Map());

  // Metadata
  const fileChangelogMap = ref<Map<string, string>>(new Map());
  const fileManagerMap = ref<Map<string, number>>(new Map());

  // External Documents
  const externalDocument = ref(false);
  const externalDocumentName = ref('');

  const uploadDuplicateTitles = ref<string[]>([]);
  const linkDuplicateTitles = ref<string[]>([]);
  const fileDocumentNameMap = ref<Map<string, string>>(new Map());
  const fileSlotIds = ref<string[]>([]);

  // ============================================
  // GETTERS
  // ============================================

  const hasUploadInProgress = computed(() => isUploading.value);

  const uploadedFilesCount = computed(() => files.value.length);

  const completedUploadsCount = computed(() => {
    let count = 0;
    uploadStatus.value.forEach((status) => {
      if (status === 'completed') count++;
    });
    return count;
  });

  const failedUploadsCount = computed(() => {
    let count = 0;
    uploadStatus.value.forEach((status) => {
      if (status === 'error') count++;
    });
    return count;
  });

  function getDocumentNameKey(file: File): string {
    const i = files.value.indexOf(file);
    if (i >= 0 && fileSlotIds.value[i]) return fileSlotIds.value[i];
    return getFileKey(file);
  }

  const uploadBatchDuplicateTitles = computed(() => {
    const titleCounts = files.value.reduce((acc, f) => {
      const name = (fileDocumentNameMap.value.get(getDocumentNameKey(f)) ?? '')
        .trim()
        .toLowerCase();
      if (name) {
        acc.set(name, (acc.get(name) ?? 0) + 1);
      }
      return acc;
    }, new Map<string, number>());
    return [...titleCounts.entries()]
      .filter(([, count]) => count > 1)
      .map(([title]) => title);
  });

  const { getDuplicatePrefixesInBatch, extractPrefixFromFileName } = useFileValidation();
  const uploadBatchDuplicatePrefixes = computed(() => {
    if (externalDocument.value) return [];
    return getDuplicatePrefixesInBatch(files.value);
  });

  const filePrefixesForConflictCheck = computed(() => {
    if (externalDocument.value) return [];
    return buildUniquePrefixList(files.value, (file) => extractPrefixFromFileName(file.name));
  });
  const { conflictingPrefixes: dbPrefixConflicts, isCheckingPrefixConflicts } =
    usePrefixConflictCheck(filePrefixesForConflictCheck);

  const canSubmitExternalUpload = computed(() => {
    if (files.value.length === 0) return false;
    if (uploadBatchDuplicateTitles.value.length > 0) return false;
    return files.value.every((f) => {
      const name = (fileDocumentNameMap.value.get(getDocumentNameKey(f)) ?? '').trim();
      return name.length >= MIN_DOCUMENT_TITLE_LENGTH;
    });
  });

  // ============================================
  // ACTIONS - File Management
  // ============================================

  function getFileKey(file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}`;
  }

  function getFileId(file: File, fileIds: Map<File, string>): string {
    return fileIds.get(file) ?? `${Date.now()}-${fileIdCounter++}-${file.name}`;
  }

  function handleFileUpload(newFiles: File[], isExternal = false, documentName = '') {
    const filesToAdd = newFiles.filter((file) => !files.value.includes(file));
    files.value = [...files.value, ...filesToAdd];
    uploadedFiles.value = [...files.value];
    hasFiles.value = files.value.length > 0;
    filesLoaded.value = files.value.length > 0;
    uploadedDocumentIds.value = [];

    const now = Date.now();
    const startIndex = fileSlotIds.value.length;
    const newSlotIds = newFiles.map((_, index) => `slot-${now}-${startIndex + index}`);
    const defaultName = files.value.length === 1 ? documentName : '';
    const nextMap = new Map(fileDocumentNameMap.value);
    newSlotIds.forEach((slotId) => {
      nextMap.set(slotId, defaultName);
    });
    fileSlotIds.value = [...fileSlotIds.value, ...newSlotIds];
    fileDocumentNameMap.value = nextMap;

    externalDocument.value = isExternal;
    externalDocumentName.value = documentName;
  }

  function updateFileDocumentName(file: File, name: string) {
    const key = getDocumentNameKey(file);
    const next = new Map(fileDocumentNameMap.value);
    next.set(key, name);
    fileDocumentNameMap.value = next;
  }

  function getDocumentNameForFile(file: File): string {
    return fileDocumentNameMap.value.get(getDocumentNameKey(file)) ?? '';
  }

  function handleFilesSelected(
    newFiles: FileList | null,
    callbacks: FileUploadCallbacks,
    isExternal = false,
    documentName = ''
  ) {
    if (!newFiles || newFiles.length === 0) return;

    const filteredFiles = callbacks.filterFiles(newFiles);

    if (filteredFiles.length === 0) {
      callbacks.showError('Somente arquivos PDF são permitidos.');
      return;
    }

    if (filteredFiles.length < newFiles.length) {
      callbacks.showError('Alguns arquivos foram ignorados. Somente arquivos PDF são permitidos.');
    }

    filteredFiles.forEach((file) => {
      const fileId = `${file.name}-${Date.now()}-${fileIdCounter++}`;
      filesIds.value.set(file, fileId);
      uploadStatus.value.set(fileId, 'completed');
    });

    files.value = [...files.value, ...filteredFiles];
    uploadedFiles.value = [...uploadedFiles.value, ...filteredFiles];
    hasFiles.value = files.value.length > 0;
    filesLoaded.value = files.value.length > 0;
    externalDocument.value = isExternal;
    externalDocumentName.value = documentName;

    if (callbacks.onFilesSelected) {
      callbacks.onFilesSelected(filteredFiles);
    }
  }

  function removeFile(fileOrIndex: File | number): number | undefined {
    let index: number;
    let file: File;

    if (typeof fileOrIndex === 'number') {
      index = fileOrIndex;
      file = files.value[index];
    } else {
      file = fileOrIndex;
      index = files.value.findIndex(
        (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
      );
    }

    if (index >= 0 && index < files.value.length) {
      const fileKey = getFileKey(file);
      const documentId = fileToDocumentIdMap.value.get(fileKey);
      const slotId = fileSlotIds.value[index];

      files.value.splice(index, 1);
      fileSlotIds.value.splice(index, 1);
      fileToDocumentIdMap.value.delete(fileKey);

      if (slotId) {
        const nextMap = new Map(fileDocumentNameMap.value);
        nextMap.delete(slotId);
        fileDocumentNameMap.value = nextMap;
      }

      if (documentId) {
        uploadedDocumentIds.value = uploadedDocumentIds.value.filter((id) => id !== documentId);
      }

      hasFiles.value = files.value.length > 0;

      return documentId;
    }
    return undefined;
  }

  // ============================================
  // ACTIONS - Progress Management
  // ============================================

  function handleFileProgress(fileId: string, progress: number) {
    fileProgress.value.set(fileId, progress);
    uploadProgress.value.set(fileId, progress);
  }

  function handleFileComplete(documentIds?: number[]) {
    let complete = true;
    fileProgress.value.forEach((progress) => {
      if (progress < 100) complete = false;
    });
    filesLoaded.value = complete;

    if (documentIds && documentIds.length > 0) {
      uploadedDocumentIds.value = documentIds;

      if (documentIds.length !== files.value.length) {
        console.warn(
          `ID de documentos inconsistentes: ${files.value.length} arquivos enviados mas ${documentIds.length} IDs de documentos recebidas`
        );
      }

      files.value.forEach((file, index) => {
        if (documentIds[index]) {
          const fileKey = getFileKey(file);
          fileToDocumentIdMap.value.set(fileKey, documentIds[index]);
        }
      });
    }
  }

  function getFileProgress(file: File): number {
    const fileId = filesIds.value.get(file);
    return fileId ? (uploadProgress.value.get(fileId) ?? 0) : 0;
  }

  function getFileStatus(file: File): 'pending' | 'uploading' | 'completed' | 'error' {
    const fileId = filesIds.value.get(file);
    return fileId ? (uploadStatus.value.get(fileId) ?? 'pending') : 'pending';
  }

  // ============================================
  // ACTIONS - Metadata Management
  // ============================================

  function updateFileChangelog(file: File, changelog: string): void {
    const fileKey = getFileKey(file);
    fileChangelogMap.value.set(fileKey, changelog);
  }

  function updateFileManager(file: File, managerId: number): void {
    const fileKey = getFileKey(file);
    fileManagerMap.value.set(fileKey, managerId);
  }

  function getDocumentIdForFile(file: File): number | undefined {
    const fileKey = getFileKey(file);
    return fileToDocumentIdMap.value.get(fileKey);
  }

  // ============================================
  // ACTIONS - Upload Operations
  // ============================================

  function setFilesUploading(files: File[], fileIds: Map<File, string>) {
    files.forEach((file) => {
      const fileId = getFileId(file, fileIds);
      uploadStatus.value.set(fileId, 'uploading');
      uploadProgress.value.set(fileId, 0);
    });
  }

  function createFormData(
    files: File[],
    replacementContext?: { documentId: number; revisionId: number }
  ): FormData {
    const formData = new FormData();

    if (replacementContext) {
      formData.append('file', files[0]);
    } else {
      files.forEach((file) => {
        formData.append('files', file);
      });

      if (!externalDocument.value) {
        const changelogs: { fileName: string; changelog: string }[] = [];
        const managers: { fileName: string; managerId: number }[] = [];

        files.forEach((file) => {
          const fileKey = getFileKey(file);
          const changelog = fileChangelogMap.value.get(fileKey);
          if (changelog) {
            changelogs.push({ fileName: file.name, changelog });
          }
          const managerId = fileManagerMap.value.get(fileKey);
          if (managerId) {
            managers.push({ fileName: file.name, managerId });
          }
        });

        if (changelogs.length > 0) {
          formData.append('changelogs', JSON.stringify(changelogs));
        }

        if (managers.length > 0) {
          formData.append('managers', JSON.stringify(managers));
        }
      }

      if (externalDocument.value) {
        const names = files.map(
          (f) => fileDocumentNameMap.value.get(getDocumentNameKey(f)) ?? externalDocumentName.value
        );
        if (names.some((n) => n.trim().length > 0)) {
          formData.append('documentNames', JSON.stringify(names));
        }
      }
    }

    return formData;
  }

  function handleUploadProgress(
    progressEvent: { loaded: number; total?: number },
    files: File[],
    fileIds: Map<File, string>,
    onUploadComplete?: (fileId: string, documentIds?: number[]) => void,
    isReplacement = false
  ) {
    if (!progressEvent.total) return;

    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);

    if (isReplacement) {
      const fileId = getFileId(files[0], fileIds);
      uploadProgress.value.set(fileId, percentCompleted);
      handleFileProgress(fileId, percentCompleted);
    } else {
      files.forEach((file) => {
        const fileId = getFileId(file, fileIds);
        uploadProgress.value.set(fileId, percentCompleted);
        handleFileProgress(fileId, percentCompleted);

        if (percentCompleted >= 99 && onUploadComplete) {
          onUploadComplete(fileId);
        }
      });
    }

    if (percentCompleted >= 99) {
      handleFileComplete();
    }
  }

  function handleUploadSuccess(
    response: { data: { data?: { id: number }[] | { id: number } } },
    files: File[],
    fileIds: Map<File, string>,
    onUploadComplete?: (fileId: string, documentIds?: number[]) => void,
    isReplacement = false
  ) {
    if (isReplacement) {
      const replacedDocument = response.data.data as { id: number } | undefined;
      if (!replacedDocument) return;

      const fileId = getFileId(files[0], fileIds);

      uploadStatus.value.set(fileId, 'completed');
      uploadProgress.value.set(fileId, 100);
      handleFileProgress(fileId, 100);

      if (onUploadComplete) {
        onUploadComplete(fileId, [replacedDocument.id]);
      }

      handleFileComplete([replacedDocument.id]);
    } else {
      const createdDocuments = (response.data.data as { id: number }[] | undefined) ?? [];
      const documentIds = createdDocuments.map((doc) => doc.id);
      uploadedDocumentIds.value = documentIds;

      files.forEach((file) => {
        const fileId = getFileId(file, fileIds);
        uploadStatus.value.set(fileId, 'completed');
        uploadProgress.value.set(fileId, 100);
        handleFileProgress(fileId, 100);

        if (onUploadComplete) {
          onUploadComplete(fileId, documentIds);
        }
      });

      handleFileComplete(documentIds);
    }
  }

  function showUploadServerErrorToast(toast: ReturnType<typeof useCustomToast>): void {
    toast.showErrorToast(
      {
        title: 'Erro no upload',
        message: 'Erro interno do servidor. Tente novamente.',
        type: 'error',
      },
      {}
    );
  }

  function handleUploadClientError(
    errorData: unknown,
    toast: ReturnType<typeof useCustomToast>
  ): void {
    const duplicateTitles = Array.isArray(
      (errorData as { duplicateTitles?: string[] })?.duplicateTitles
    )
      ? (errorData as { duplicateTitles: string[] }).duplicateTitles
      : [];
    if (duplicateTitles.length > 0) {
      uploadDuplicateTitles.value = duplicateTitles;
      toast.showErrorToast(
        {
          title: 'Erro no upload',
          message:
            (errorData as { message?: string })?.message ??
            EXTERNAL_DOCUMENT_TITLE_ALREADY_EXISTS_MESSAGE,
          type: 'error',
        },
        {}
      );
      return;
    }
    const data = errorData as { errors?: unknown; message?: string };
    if (data?.errors && typeof data.errors === 'object') {
      toast.showValidationErrorToast(
        data.errors as Record<string, string[]>,
        data.message ?? 'Erros no envio do documento'
      );
      return;
    }
    toast.showErrorToast(
      {
        title: 'Erro no upload',
        message: data?.message ?? 'Erro ao fazer upload dos arquivos',
        type: 'error',
      },
      {}
    );
  }

  function handleUploadError(error: unknown, files: File[], fileIds: Map<File, string>) {
    const toast = useCustomToast();
    files.forEach((file) => {
      const fileId = getFileId(file, fileIds);
      uploadStatus.value.set(fileId, 'error');
      uploadProgress.value.set(fileId, 0);
    });

    if (isNetworkErrorLike(error)) {
      const count = files.length;
      toast.showErrorToast(
        {
          title: getSendErrorTitle(count),
          message: getNetworkErrorMessage(count),
          type: 'error',
        },
        {}
      );
      return;
    }

    if (isAxiosError(error) && error.response) {
      const isClientError = error.response.status === 400 || error.response.status === 422;
      if (isClientError) {
        handleUploadClientError(error.response.data, toast);
      } else {
        showUploadServerErrorToast(toast);
      }
    } else {
      showUploadServerErrorToast(toast);
    }
  }

  async function uploadFiles(
    files: File[],
    fileIds: Map<File, string>,
    onUploadComplete?: (fileId: string, documentIds?: number[]) => void,
    replacementContext?: { documentId: number; revisionId: number }
  ) {
    uploadDuplicateTitles.value = [];
    isUploading.value = true;
    setFilesUploading(files, fileIds);
    const formData = createFormData(files, replacementContext);

    try {
      if (replacementContext) {
        const response = await documentApi.replaceRevision(
          replacementContext.documentId,
          replacementContext.revisionId,
          formData,
          (progressEvent: any) =>
            handleUploadProgress(progressEvent, files, fileIds, onUploadComplete, true)
        );
        handleUploadSuccess(response, files, fileIds, onUploadComplete, true);
      } else {
        const firstFileName =
          files.length > 0
            ? fileDocumentNameMap.value.get(getDocumentNameKey(files[0])) ?? externalDocumentName.value
            : externalDocumentName.value;
        const queryDocumentName = formData.has('documentNames')
          ? undefined
          : firstFileName;
        const response = await documentApi.upload(
          formData,
          (progressEvent: any) =>
            handleUploadProgress(progressEvent, files, fileIds, onUploadComplete, false),
          externalDocument.value,
          queryDocumentName
        );
        handleUploadSuccess(response, files, fileIds, onUploadComplete, false);
      }
    } catch (error) {
      handleUploadError(error, files, fileIds);
      throw error; // Re-throw the error so uploadSelectedFiles can catch it
    } finally {
      isUploading.value = false;
    }
  }

  async function uploadSelectedFiles(
    onUploadComplete?: (fileId: string, documentIds?: number[]) => void,
    replacementContext?: { documentId: number; revisionId: number }
  ) {
    if (files.value.length === 0) return;

    await uploadFiles(files.value, filesIds.value, onUploadComplete, replacementContext);
  }

  function buildLinkPayload(
    urlData: Array<{
      url: string;
      name: string;
      changelog?: string;
      managerId?: number;
    }>,
    isExternal: boolean,
    documentName?: string
  ) {
    const urls = urlData.map((data) => ({ url: data.url, name: data.name }));
    const changelogs: Array<{ fileName: string; changelog: string }> = [];
    const managers: Array<{ fileName: string; managerId: number }> = [];
    urlData.forEach((data) => {
      if (data.changelog) {
        changelogs.push({ fileName: data.name, changelog: data.changelog });
      }
      if (data.managerId) {
        managers.push({ fileName: data.name, managerId: data.managerId });
      }
    });
    const payload: {
      urls: Array<{ url: string; name: string }>;
      changelogs?: string;
      managers?: string;
      external?: boolean;
      documentName?: string;
    } = { urls, external: isExternal };
    if (!isExternal) {
      if (changelogs.length > 0) payload.changelogs = JSON.stringify(changelogs);
      if (managers.length > 0) payload.managers = JSON.stringify(managers);
    } else if (documentName) {
      payload.documentName = documentName;
    }
    return payload;
  }

  async function linkUrls(
    urlData: Array<{
      url: string;
      name: string;
      changelog?: string;
      managerId?: number;
    }>,
    isExternal = false,
    documentName?: string
  ): Promise<number[]> {
    linkDuplicateTitles.value = [];
    isUploading.value = true;
    uploadedDocumentIds.value = [];

    try {
      const payload = buildLinkPayload(urlData, isExternal, documentName);
      const response = await documentApi.link(payload);
      if (response.data?.data) {
        const documentIds = response.data.data.map((doc: { id: number }) => doc.id);
        uploadedDocumentIds.value = documentIds;
        return documentIds;
      }
      return [];
    } catch (error: unknown) {
      const toast = useCustomToast();
      const count = urlData.length;
      let errorMessage =
        'Não foi possível enviar os documentos via URL. Tente novamente ou entre em contato com o suporte.';

      if (isNetworkErrorLike(error)) {
        errorMessage = getNetworkErrorMessage(count);
      } else if (isAxiosError(error) && error.response?.data) {
        const data = error.response.data;
        if (Array.isArray(data.duplicateTitles) && data.duplicateTitles.length > 0) {
          linkDuplicateTitles.value = data.duplicateTitles;
          errorMessage = data.message ?? EXTERNAL_DOCUMENT_TITLE_ALREADY_EXISTS_MESSAGE;
        } else if (data.message) {
          const raw = data.message as string;
          errorMessage =
            raw === 'Network Error' || raw?.includes('Network Error')
              ? getNetworkErrorMessage(count)
              : raw;
        }
      }

      toast.showErrorToast({
        title: getSendErrorTitle(count),
        message: errorMessage,
        type: 'error',
      });

      throw error;
    } finally {
      isUploading.value = false;
    }
  }

  async function deleteDocuments(documentIds: number[]): Promise<DeleteResult> {
    const deletedIds: number[] = [];
    const messages: string[] = [];

    for (const documentId of documentIds) {
      try {
        const response = await documentApi.deleteDocument(documentId);
        deletedIds.push(documentId);

        const responseData = response.data as { message?: string } | undefined;
        if (responseData?.message) {
          messages.push(responseData.message);
        }
      } catch (error: unknown) {
        let errorMessage =
          'Não foi possível excluir o documento. Tente novamente ou entre em contato com o suporte.';

        if (isAxiosError(error) && error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }

        throw new Error(errorMessage);
      }
    }

    return { deletedIds, messages };
  }

  // ============================================
  // ACTIONS - State Management
  // ============================================

  function cleanState() {
    files.value = [];
    uploadedFiles.value = [];
    hasFiles.value = false;
    filesLoaded.value = false;
    isUploading.value = false;
    isUrlUploadValid.value = false;
    fileProgress.value.clear();
    uploadProgress.value.clear();
    uploadStatus.value.clear();
    uploadedDocumentIds.value = [];
    fileToDocumentIdMap.value.clear();
    fileChangelogMap.value.clear();
    filesIds.value.clear();
    externalDocument.value = false;
    externalDocumentName.value = '';
    fileManagerMap.value.clear();
    uploadDuplicateTitles.value = [];
    linkDuplicateTitles.value = [];
    fileDocumentNameMap.value = new Map();
    fileSlotIds.value = [];
  }

  // ============================================
  // RETURN
  // ============================================

  return {
    // State
    files,
    uploadedFiles,
    filesIds,
    hasFiles,
    isUrlUploadValid,
    filesLoaded,
    isUploading,
    fileProgress,
    uploadProgress,
    uploadStatus,
    uploadedDocumentIds,
    fileToDocumentIdMap,
    fileChangelogMap,
    fileManagerMap,
    externalDocument,
    externalDocumentName,
    uploadDuplicateTitles,
    linkDuplicateTitles,
    fileDocumentNameMap,
    uploadBatchDuplicateTitles,
    uploadBatchDuplicatePrefixes,
    dbPrefixConflicts,
    isCheckingPrefixConflicts,
    canSubmitExternalUpload,

    // Getters
    hasUploadInProgress,
    uploadedFilesCount,
    completedUploadsCount,
    failedUploadsCount,

    // Actions
    handleFileUpload,
    handleFileProgress,
    handleFileComplete,
    handleFilesSelected,
    removeFile,
    getFileProgress,
    getFileStatus,
    getDocumentIdForFile,
    updateFileChangelog,
    updateFileManager,
    updateFileDocumentName,
    getDocumentNameForFile,
    uploadFiles,
    uploadSelectedFiles,
    linkUrls,
    deleteDocuments,
    cleanState,

    // Utility (internal use)
    getFileKey,
    getFileId,
    setFilesUploading,
    createFormData,
    handleUploadProgress,
    handleUploadSuccess,
    handleUploadError,
  };
});
